import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, CalendarIcon, Users, Edit, Clock } from "lucide-react";
import { ImpactedReservationByTimeSlot } from "@/lib/timeSlotImpactUtils";

interface TimeSlotImpactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timeSlotLabel: string;
  impactedReservations: ImpactedReservationByTimeSlot[];
  actionType: 'modify' | 'delete' | 'deactivate';
  onEditReservation: (reservationId: string) => void;
  onConfirm: () => void;
}

export function TimeSlotImpactDialog({
  open,
  onOpenChange,
  timeSlotLabel,
  impactedReservations,
  actionType,
  onEditReservation,
  onConfirm,
}: TimeSlotImpactDialogProps) {
  const getBadgeVariant = (reason: 'time_changed' | 'day_unavailable' | 'slot_inactive') => {
    if (reason === 'time_changed') return 'destructive';
    if (reason === 'day_unavailable') return 'secondary';
    return 'outline';
  };

  const getBadgeText = (reason: 'time_changed' | 'day_unavailable' | 'slot_inactive') => {
    if (reason === 'time_changed') return '⏰ Horaire modifié';
    if (reason === 'day_unavailable') return '📅 Jour non disponible';
    return '⏸️ Créneau désactivé';
  };

  const getActionTitle = () => {
    if (actionType === 'delete') return 'Attention : réservations impactées';
    if (actionType === 'deactivate') return 'Attention : réservations impactées';
    return 'Attention : réservations impactées';
  };

  const getActionMessage = () => {
    if (actionType === 'delete') {
      return (
        <>
          <strong className="text-amber-700 dark:text-amber-400">⚠️ Cette suppression peut affecter des réservations.</strong>
          <br /><br />
          Il y a <strong>{impactedReservations.length} réservation{impactedReservations.length > 1 ? 's' : ''} future{impactedReservations.length > 1 ? 's' : ''}</strong> qui 
          utilise{impactedReservations.length > 1 ? 'nt' : ''} ce créneau <strong>{timeSlotLabel}</strong>.
          <br /><br />
          Vous pouvez continuer si vous êtes sûr de cette action, ou annuler pour modifier d'abord les réservations concernées.
        </>
      );
    }
    
    if (actionType === 'deactivate') {
      return (
        <>
          <strong className="text-amber-700 dark:text-amber-400">⚠️ Cette désactivation peut affecter des réservations.</strong>
          <br /><br />
          Il y a <strong>{impactedReservations.length} réservation{impactedReservations.length > 1 ? 's' : ''} future{impactedReservations.length > 1 ? 's' : ''}</strong> qui 
          utilise{impactedReservations.length > 1 ? 'nt' : ''} ce créneau <strong>{timeSlotLabel}</strong>.
          <br /><br />
          Vous pouvez continuer si vous êtes sûr de cette action, ou annuler pour modifier d'abord les réservations concernées.
        </>
      );
    }

    return (
      <>
        <strong className="text-amber-700 dark:text-amber-400">⚠️ Cette modification peut affecter des réservations.</strong>
        <br /><br />
        Il y a <strong>{impactedReservations.length} réservation{impactedReservations.length > 1 ? 's' : ''} future{impactedReservations.length > 1 ? 's' : ''}</strong> qui 
        utilise{impactedReservations.length > 1 ? 'nt' : ''} actuellement ce créneau <strong>{timeSlotLabel}</strong>.
        <br /><br />
        Vous pouvez continuer si vous êtes sûr de cette action, ou annuler pour modifier d'abord les réservations concernées.
      </>
    );
  };

  const getConfirmButtonText = () => {
    if (actionType === 'delete') return 'Supprimer quand même';
    if (actionType === 'deactivate') return 'Désactiver quand même';
    return 'Modifier quand même';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {getActionTitle()}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-300">
              {getActionMessage()}
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4" />
              📋 {impactedReservations.length} réservation{impactedReservations.length > 1 ? 's' : ''} concernée{impactedReservations.length > 1 ? 's' : ''} :
            </h4>

            <ScrollArea className={impactedReservations.length > 4 ? "h-[320px]" : ""}>
              <div className="space-y-2 pr-4">
                {impactedReservations.map((reservation) => (
                  <Card key={reservation.id} className="border-muted">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-1 flex-1">
                            <p className="font-medium">🍽️ {reservation.customerName}</p>
                            <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-3 w-3" />
                                <span>{reservation.date} à {reservation.startTime}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="h-3 w-3" />
                                <span>{reservation.numberOfPeople} personne{reservation.numberOfPeople > 1 ? 's' : ''}</span>
                              </div>
                              <div className="text-xs font-medium text-primary">
                                📍 {reservation.tableName}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              onEditReservation(reservation.id);
                              onOpenChange(false);
                            }}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Modifier
                          </Button>
                        </div>
                        <Badge variant={getBadgeVariant(reservation.impactReason)}>
                          {getBadgeText(reservation.impactReason)}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>💡 Conseil :</strong>
              <br />
              Si vous souhaitez d'abord gérer les réservations concernées, utilisez le bouton <strong>"Modifier"</strong> pour chaque réservation afin de :
              <ul className="list-disc list-inside mt-2 ml-2">
                <li>Changer l'horaire vers un autre créneau disponible</li>
                <li>Réassigner à une autre table si nécessaire</li>
                <li>Ou annuler la réservation si elle n'est plus valide</li>
              </ul>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            variant="destructive" 
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            {getConfirmButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
